import { Accordion, AccordionTab } from "primereact/accordion";
import PanelSection from "../Template/panelSection";
import { SelectButton } from "primereact/selectbutton";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import SelectedExercisesItemTemplate from "../Template/selectedExercisesItemTemplate";
import { Calendar } from "primereact/calendar";
import { useOutletContext } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import { purifyPanelData } from "../../purifyExerciseMethods";
import ExerciseEditSection from "./exerciseEditSection";
import { SELECTBUTTON_OPTIONS } from "../../exerciseDefaults";
import { formatCarePlanName } from "../../format";

const AddExercisesPanelsSection = ({
  t,
  isPanelsSectionSelected,
  selectedPatient,
  onHideDialog,
  handleDelete,
  carePlanName,
  setCarePlanName,
  handleCarePlanName,
  dateOfStart,
  handleDateOfStart,
  handleSelectButton,
  dateOfEnd,
  handleDateOfEnd,
  isPendingAddPlan,
  handleApprovePrescription,
  handleDeleteAll,
  setExerciseVideoDialogVisible,
  setSelectedExerciseForVideo,
  selectedExercises,
  setSelectedExercises,
}: any) => {
  const { systemPanels, myPanels }: any = useOutletContext();

  const [activeIndex, setActiveIndex] = useState<number[]>([0, 1]);
  const [isEditModeActive, setIsEditModeActive] = useState<boolean>(false);
  const [isCollapsedDetails, setIsCollapsedDetails] = useState<boolean>(false);
  const [selectedExercise, setSelectedExercise] = useState<any>();
  const [searchExercise, setSearchExercise] = useState<string>("");
  const [editedExercises, setEditedExercises] = useState<any[]>([]);

  const debouncedValue = useDebounce(searchExercise);

  const handleSelectPanel = (panels: any) => {
    setSelectedExercise(undefined);

    const updatedExercises = purifyPanelData(panels?.exerciseProcedure)

    setSelectedExercises((prevSelectedExercises:any) => {
      const filteredExercises = updatedExercises.filter(
        (exercise: any) => !prevSelectedExercises.some((selected:any) => selected.exercise.id === exercise.exercise.id)
      );
      return [...prevSelectedExercises, ...filteredExercises];
    });

    if (carePlanName.trim() === "") {
      let panelName: string = panels?.name ?? "";
      let carePlanName = formatCarePlanName(panelName);
      setCarePlanName(carePlanName);
    }
  }

  const handleAddExercise = (exerciseProcedure: any) => {
    if (selectedExercises.some((selected:any) => selected.exercise.id === exerciseProcedure.procedureId)) {
      return;
    }
    const purified = purifyPanelData([exerciseProcedure])[0];
    setSelectedExercises([...selectedExercises, purified]);
  }

  const myPanelsMemo = useMemo(() => {
    if (!debouncedValue) return myPanels;
    return myPanels.filter((panel: any) => panel.name.toLowerCase().includes(debouncedValue.toLowerCase()));
  }, [debouncedValue, myPanels])

  const systemPanelsMemo = useMemo(() => {
    if (!debouncedValue) return systemPanels;
    return systemPanels.filter((panel: any) => panel.name.toLowerCase().includes(debouncedValue.toLowerCase()));
  }, [debouncedValue, systemPanels])

  const handleSaveEdit = () => {
    setSelectedExercises(editedExercises);
    setEditedExercises([]);
    setIsEditModeActive(false);
    setIsCollapsedDetails(false);
    setSelectedExercise(null);
  };

  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if(isEditModeActive) return;
      setSelectedExercises((prev: any) => {
        const newExerciseProcedure = [...prev];
        const [movedItem] = newExerciseProcedure.splice(dragIndex, 1);
        newExerciseProcedure.splice(hoverIndex, 0, movedItem);
        return newExerciseProcedure;
      });
    },
    [setSelectedExercises, isEditModeActive]
  );

  const myAndSystemAccordionTabHeaderTemplate = (name: string, tabIndex: number) => {
    return (
      <div className="flex align-items-center gap-3">
        <span>{name}</span>
        <i className={`${activeIndex.includes(tabIndex) ? "pi pi-chevron-up" : "pi pi-chevron-down"}`}></i>
      </div>
    )
  }

  const selectButtonTemplate = (option: any) => {
    const img = option.name === "panels" ? "/panels-menu-img-<color>.svg" : "/wizard-menu-img-<color>.svg";
    const imgSrc = isPanelsSectionSelected ? img.replace("<color>", "white") : img.replace("<color>", "gray");
    return (
      <div className="flex align-items-center gap-2">
        <img src={imgSrc} alt={option.name} width={25} />
        <span>{t(option.name)}</span>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden" style={{ backgroundColor: "#EBEDF2" }}>
      <div className="flex flex-column gap-3 bg-white p-4 w-full relative" style={{ borderRadius: "24px" }}>
        <div className="flex flex-column justify-content-start gap-2">
          <div className="flex flex-wrap justify-content-between align-items-center gap-3">
            <div className="flex flex-grow-1 flex-wrap justify-content-between gap-3">
              <SelectButton allowEmpty={false} options={SELECTBUTTON_OPTIONS} itemTemplate={selectButtonTemplate} optionLabel="name" optionValue="value" className="exercises-or-panels-select-button flex gap-3" value={isPanelsSectionSelected} onChange={handleSelectButton} />
            </div>
            <IconField iconPosition="right">
              <InputIcon className="pi pi-search"></InputIcon>
              <InputText className="flex-grow-1 h-3rem" value={searchExercise} onChange={(e:any) => setSearchExercise(e.target.value)} id="searchPanel" placeholder={t("searchPanel")} style={{ borderRadius: "10px", border: "1px solid rgba(158, 164, 185, 1)", width: "16.5rem" }} />
            </IconField>
          </div>
        </div>
        <div className="flex flex-column gap-5 h-full overflow-auto">
          <Accordion multiple className="myAndSystemPanelsAccordion flex flex-column gap-3" activeIndex={activeIndex} onTabChange={(e: any) => setActiveIndex(e.index)}>
            <AccordionTab headerTemplate={() => myAndSystemAccordionTabHeaderTemplate(t("myPanels"), 0)} tabIndex={0}>
              <div className="flex flex-column gap-3 overflow-auto">
                <PanelSection t={t} myPanels={myPanelsMemo} handleSelectPanel={handleSelectPanel} handleAddExercise={handleAddExercise} />
              </div>
            </AccordionTab>
            <AccordionTab headerTemplate={() => myAndSystemAccordionTabHeaderTemplate(t("systemPanels"), 1)} tabIndex={1}>
              <div className="flex flex-column gap-3 overflow-auto flex-grow-1">
                <PanelSection t={t} myPanels={systemPanelsMemo} handleSelectPanel={handleSelectPanel} handleAddExercise={handleAddExercise} />
              </div>
            </AccordionTab>
          </Accordion>
        </div>
      </div>
      <div className="relative w-full h-full" style={{ backgroundColor: "#EBEDF2" }}>
        <div className="flex flex-column justify-content-between gap-3 p-4 w-full h-full overflow-y-auto">
          <div className="flex flex-column justify-content-start pt-2 flex-grow-1 gap-3">
            <div className="flex justify-content-between align-items-center">
              <span className="text-2xl font-bold" style={{ color: "#727A99" }}>{t("prescription")} | <span style={{ color: "#E3506B" }}>{selectedPatient.firstName + " " + selectedPatient.lastName}</span></span>
              <Button onClick={onHideDialog} className="w-2rem h-2rem" style={{ border: "1.5px solid #727A99", color: "#727A99", backgroundColor: "#EBEDF2" }} icon="pi pi-times" outlined rounded aria-label="Delete" />
            </div>
            {selectedExercises.length > 0 && (
              <div className="flex justify-content-between align-items-center">
                <div className="total-exercises-badge">
                  <span>{t("totalPrescribedExercises")}:</span>
                  <span className="total-exercises-count">{selectedExercises.length}</span>
                </div>
                {isEditModeActive ? (
                  <div className="flex align-items-center gap-3">
                    <Button
                      style={{ borderRadius: "10px", height: "3rem" }}
                      link
                      className="p-0"
                      onClick={handleSaveEdit}
                    >
                      <span className="flex justify-content-center align-items-center gap-2 font-semibold" style={{ borderBottom: "1px solid rgba(99, 131, 244, 1)", width: "100%", color: "rgba(99, 131, 244, 1)", fontSize: "1rem" }}>
                        <i className="pi pi-save"></i>
                        {t("save")}
                      </span>
                    </Button>
                    <Button
                      style={{ borderRadius: "10px", height: "3rem" }}
                      link
                      className="p-0"
                      onClick={() => {
                        setSelectedExercise(null);
                        setIsCollapsedDetails(false);
                        setIsEditModeActive(false);
                        setEditedExercises([]);
                      }}
                    >
                      <span className="flex justify-content-center align-items-center gap-2 font-semibold" style={{ borderBottom: "1px solid rgba(99, 131, 244, 1)", width: "100%", color: "rgba(99, 131, 244, 1)", fontSize: "1rem" }}>
                        <i className="pi pi-times"></i>
                        {t("cancel")}
                      </span>
                    </Button>
                  </div>
                ) : (
                  <Button
                    style={{ borderRadius: "10px", height: "3rem" }}
                    link
                    onClick={() => {
                      setEditedExercises(selectedExercises)
                      setIsEditModeActive(true)
                    }}
                  >
                    <span className="flex justify-content-center align-items-center gap-2 font-semibold" style={{ borderBottom: "1px solid rgba(99, 131, 244, 1)", width: "100%", color: "rgba(99, 131, 244, 1)", fontSize: "1rem" }}>
                      <i className="pi pi-pencil"></i>
                      {t("editPanel")}
                    </span>
                  </Button>
                )}
              </div>
            )}
            <div className="flex flex-wrap justify-content-between align-content-start flex-grow-1" style={{ height: "20rem", overflowY: "auto", overflowX: "hidden" }}>
              {(isEditModeActive ? editedExercises : selectedExercises).map((exercise: any, index:number) => (
                <SelectedExercisesItemTemplate
                  key={exercise.exercise.name}
                  exercise={exercise}
                  handleDelete={handleDelete}
                  isEditModeActive={isEditModeActive}
                  setExerciseVideoDialogVisible={setExerciseVideoDialogVisible}
                  setSelectedExercise={setSelectedExercise}
                  setSelectedExerciseForVideo={setSelectedExerciseForVideo}
                  setIsCollapsedDetails={setIsCollapsedDetails}
                  t={t}
                  index={index}
                  moveCard={moveCard} />
              ))}
            </div>
          </div>
          <div className="">
            <div className="flex justify-content-end align-items-center">
              {!isEditModeActive && (
                <Button
                  style={{ borderRadius: "10px", height: "3rem" }}
                  onClick={handleDeleteAll}
                  disabled={selectedExercises.length === 0}
                  link
                >
                  <span className="flex justify-content-center align-items-center gap-2 font-semibold" style={{ borderBottom: "1px solid rgba(99, 131, 244, 1)", width: "100%", color: "rgba(99, 131, 244, 1)", fontSize: "1rem" }}>
                    <img src="/trashIcon.svg" />
                    {t("clearAll")}
                  </span>
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="flex w-full flex-column gap-1">
                <label htmlFor="planName" style={{ color: "#727A99" }}>{t("carePlanName")} <span style={{ color: "#E3506B" }}>*</span></label>
                <InputText className="care-plan-name-inputtext" value={carePlanName} onChange={handleCarePlanName} id="planName" placeholder={t("patientCarePlan")} style={{ borderRadius: "10px", border: "1px solid #9EA4B9" }} onInput={(e: any) => {
                  handleCarePlanName({ target: { value: e.currentTarget.value } });
                }} />
              </div>
              <div className="flex gap-3 w-full">
                <div className="flex flex-column gap-1 w-full">
                  <label htmlFor="dateOfStart" style={{ color: "#727A99" }}>
                    {t("dateOfStart")} <span style={{ color: "#E3506B" }}>*</span>
                  </label>
                  <Calendar className="add-exercise-dialog-calendar" value={dateOfStart} id="dateOfStart" onChange={handleDateOfStart} minDate={new Date()} maxDate={dateOfEnd} />
                </div>
                <div className="flex flex-column gap-1 w-full">
                  <label htmlFor="dateOfEnd" style={{ color: "#727A99" }}>
                    {t("dateOfEnd")} <span style={{ color: "#E3506B" }}>*</span>
                  </label>
                  <Calendar className="add-exercise-dialog-calendar" value={dateOfEnd} id="dateOfEnd" onChange={handleDateOfEnd} minDate={dateOfStart} maxDate={new Date(new Date().getTime() + 90 * 24 * 60 * 60 * 1000)} />
                </div>
              </div>
              <div className="flex justify-content-end align-items-center gap-3 w-full">
                <Button
                  style={{ backgroundColor: "rgba(99, 131, 244, 1)", border: "none", borderRadius: "10px", height: "3rem" }}
                  disabled={selectedExercises.length === 0 || carePlanName === "" || isEditModeActive}
                  onClick={handleApprovePrescription}
                  tooltip={selectedExercises.length === 0 ? t("addExerciseTooltip") : carePlanName === "" ? t("enterPlanNameTooltip") : ""}
                  tooltipOptions={{ position: "bottom", showOnDisabled: true, showDelay: 300 }}
                  loading={isPendingAddPlan}
                >
                  <span className="flex justify-content-center align-items-center gap-2 font-bold" style={{ width: "100%" }}>
                    {!isPendingAddPlan && <img src="/Check.svg" />}
                    {t("approvePrescription")}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
        {(isCollapsedDetails && selectedExercise) && (
          <ExerciseEditSection 
            t={t}
            selectedExercise={selectedExercise}
            setSelectedExercise={setSelectedExercise}
            isEditModeActive={isEditModeActive}
            setIsCollapsedDetails={setIsCollapsedDetails}
            editedExercises={editedExercises}
            setEditedExercises={setEditedExercises}
          />
        )}
      </div>
    </div>
  )
}

export default AddExercisesPanelsSection;